import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Menu, X, Search } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";

const links = [
  { label: "Home", target: "#top", spy: ['[data-testid="hero-section"]'] },
  { label: "Brand Story", target: "#story", spy: ["#story"] },
  { label: "Product Category", target: "#catalogue", spy: ["#categories"] },
  { label: "Contact us", target: "#contact", spy: ["#contact"] },
  { label: "Brochure", target: "brochure", spy: [] },
];

const API = process.env.REACT_APP_BACKEND_URL;

export const downloadBrochure = async () => {
  try {
    const res = await fetch(`${API}/api/brochure/info`);
    const info = await res.json();
    if (!info.available) {
      const { toast } = await import("sonner");
      toast.error("Brochure abhi available nahi hai");
      return;
    }
    window.location.href = `${API}/api/brochure`;
  } catch {
    window.location.href = `${API}/api/brochure`;
  }
};

export const scrollToId = (target) => {
  const el = target === "#top" ? document.body : document.querySelector(target);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(target === "#top" ? 0 : el, { offset: -70 });
  else if (target === "#top") window.scrollTo({ top: 0, behavior: "smooth" });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = ({ query = "", setQuery = () => {} }) => {
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [active, setActive] = useState("Home");
  const navigate = useNavigate();
  const location = useLocation();

  const onSearch = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (v.trim().length >= 1) {
      if (location.pathname !== "/products") navigate("/products");
      else scrollToId("#catalogue");
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (location.pathname !== "/products") navigate("/products");
      setTimeout(() => scrollToId("#catalogue"), 80);
    }
  };

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const link = links.find((l) => l.spy.includes(e.target.dataset.spy));
            if (link) setActive(link.label);
          }
        });
      },
      { rootMargin: "-35% 0px -55% 0px" }
    );
    links.forEach((l) => {
      l.spy.forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) {
          el.dataset.spy = sel;
          obs.observe(el);
        }
      });
    });
    return () => obs.disconnect();
  }, []);

  const go = (target) => {
    setMenuOpen(false);
    if (target === "brochure") {
      downloadBrochure();
      return;
    }
    if (target === "#catalogue") {
      navigate("/products");
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: target } });
    else scrollToId(target);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/75 border-b border-[#221533]/10"
      data-testid="main-navbar"
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-12 h-[70px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="font-clash text-2xl sm:text-3xl font-semibold tracking-tight text-[#74427F]"
        >
          ZALTA<sup className="text-[#E63946] text-xs align-super">®</sup>
        </button>
        <nav className="hidden lg:flex items-center gap-2">
          {links.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => go(l.target)}
              className={`font-manrope font-semibold text-xs tracking-[0.12em] uppercase px-5 py-2.5 rounded-full transition-[background-color,color,box-shadow] duration-300 active:scale-95 ${
                active === l.label
                  ? "bg-[#8E5A9E] text-white shadow-[0_10px_25px_-8px_rgba(142,90,158,0.6)]"
                  : "text-[#221533]/75 hover:bg-[#8E5A9E] hover:text-white hover:shadow-[0_10px_22px_-8px_rgba(142,90,158,0.5)]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center gap-2 bg-[#8E5A9E] hover:bg-[#74427F] text-white px-4 sm:px-5 py-2.5 rounded-md transition-colors duration-300 active:scale-95"
          >
            <ShoppingCart size={16} />
            <span className="font-mono2 text-[11px] tracking-[0.18em] uppercase hidden sm:inline">Order now</span>
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-2 -right-2 bg-[#E63946] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-search-mobile-btn"
            onClick={() => {
              setMenuOpen(false);
              setSearchOpen((v) => !v);
            }}
            className="md:hidden w-10 h-10 flex items-center justify-center border border-[#221533]/15 rounded-md text-[#221533]"
            aria-label="Search products"
          >
            <Search size={16} />
          </button>
          <button
            data-testid="nav-hamburger"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-[#221533]/15 rounded-md text-[#221533]"
            aria-label="Menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="hidden md:flex w-48 lg:w-60 relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#221533]/40" />
            <input
              data-testid="nav-search-input"
              value={query}
              onChange={onSearch}
              onKeyDown={onSearchKeyDown}
              placeholder="Search products…"
              className="w-full bg-white border border-[#221533]/15 rounded-full pl-10 pr-4 py-2.5 text-sm text-[#221533] placeholder-[#221533]/40 focus:border-[#8E5A9E] transition-colors duration-300"
            />
          </div>
        </div>
      </div>
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            data-testid="mobile-search-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-[#221533]/10"
          >
            <div className="px-5 py-3">
              <div className="relative">
                <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#221533]/40" />
                <input
                  data-testid="nav-search-input-mobile"
                  autoFocus
                  value={query}
                  onChange={onSearch}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Search products…"
                  className="w-full bg-[#FAF8FC] border border-[#221533]/15 rounded-full pl-11 pr-4 py-3 text-sm text-[#221533] placeholder-[#221533]/40 focus:border-[#8E5A9E] transition-colors"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-[#221533]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.target}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => go(l.target)}
                  className={`text-left font-clash text-2xl font-medium transition-colors px-4 py-2 rounded-lg ${
                    active === l.label
                      ? "bg-[#8E5A9E] text-white"
                      : "text-[#221533] hover:text-[#74427F]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-order"
                onClick={() => { setMenuOpen(false); setOpen(true); }}
                className="mt-2 bg-[#8E5A9E] text-white font-mono2 text-xs tracking-[0.2em] uppercase px-6 py-4 rounded-md"
              >
                Order now
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
